import { Milliseconds } from '@iotv/iotv-v3-types'
import { GetTimeBucketForTimestampInput, TimeBucketType, TimeBucketUnits } from './types'
import { getNiceDate } from '..'

const debug = process.env.REACT_APP_DEBUG && false

const yearZero = 0

/** returns the  */
export const getTimeBucketStartDateTimeForTimestamp = ({ timestamp, timeBucketDef }: GetTimeBucketForTimestampInput): Date => {
  if ([TimeBucketUnits.MONTH, TimeBucketUnits.YEAR].includes(timeBucketDef.unit)) {
    return getVariablePeriodTimeBucketStartDateForTimestamp({ timestamp, timeBucketDef })
  } else {
    return getPredictableTimeBucketStartDateTimeForTimestamp({ timestamp, timeBucketDef })
  }
}

const getVariablePeriodTimeBucketStartDateForTimestamp = ({ timestamp, timeBucketDef }: GetTimeBucketForTimestampInput): Date => {
  const { width, unit } = timeBucketDef
  let returnDate = new Date('1970-01-01 00:00:00 UTC')
  const tsDate = new Date(timestamp)

  // Find start of containing year / month
  if (unit === TimeBucketUnits.YEAR) {
    const tsYear = tsDate.getUTCFullYear()
    const q = Math.floor((tsYear - yearZero) / width)
    const bucketYear = q * width
    returnDate.setUTCFullYear(bucketYear)
  } else if (unit === TimeBucketUnits.MONTH) {
    const yearZeroYrDiff = tsDate.getUTCFullYear() - returnDate.getUTCFullYear()
    const yearZeroMnDiff = tsDate.getUTCMonth() - returnDate.getUTCMonth()
    const monthsDiff = 12 * yearZeroYrDiff + yearZeroMnDiff
    const q = Math.floor(monthsDiff / width)
    const bucketMonthYrZRelative = q * width
    returnDate.setUTCMonth(returnDate.getUTCMonth() + bucketMonthYrZRelative)
  }

  // If offset, find the component in surplus of the latest whole year/month
  // If the surplus is less than the offset, the bucket needs to be one year/month behind
  if (timeBucketDef.offset) {
    const surplusComponent = new Date(timestamp).valueOf() - returnDate.valueOf()

    if (surplusComponent < timeBucketDef.offset) {
      // Should be <= ???
      if (unit === TimeBucketUnits.YEAR) {
        returnDate.setUTCFullYear(returnDate.getUTCFullYear() - 1)
      } else if (unit === TimeBucketUnits.MONTH) {
        returnDate.setUTCMonth(returnDate.getUTCMonth() - 1)
      }
    }

    const returnDateMS = returnDate.valueOf()
    returnDate = new Date(returnDateMS + timeBucketDef.offset)
  }

  return returnDate
}

const getPredictableTimeBucketStartDateTimeForTimestamp = ({ timestamp, timeBucketDef }: GetTimeBucketForTimestampInput): Date => {
  const { width, unit } = timeBucketDef

  const period = width * unit
  debug && console.log('Get Time Bucket Start: Period', period, period / Milliseconds.HOUR)
  // Number of periods since the start of time, (which is 00:00 UTC)
  const numWholePeriodsSinceBucketStart = Math.floor(timestamp / period)

  // The number of whole buckets since the start of time is the bucket start timestamp
  // This bucket start is then offset by the offset
  // This adjustment can mean our timestamp is now before the bucket start. In this case, the bucket start is actually one bucket prior
  let bucketStartTimestamp = period * numWholePeriodsSinceBucketStart + (timeBucketDef.offset ?? 0)
  debug && console.log('Get Time Bucket Start: timestamp found', bucketStartTimestamp, getNiceDate(bucketStartTimestamp))
  if (bucketStartTimestamp > timestamp) {
    //debug && console.log("Get Time Bucket Start: need to adjust by period");
    bucketStartTimestamp -= period // If offset is a certain number, we need to adjust. It's > and not >= because buckets are exclsuive of their end times
  }

  return new Date(bucketStartTimestamp)

  /*
    const tp = timestamp - period
    const baseline = new Date( tp )
    baseline.setUTCMonth( 0 )
    baseline.setUTCDate( 1 )
    baseline.setUTCHours( 0, 0, 0, 0 )
    let ty = baseline.valueOf();
    if (timeBucketDef.offset) {
        ty += timeBucketDef.offset
    }
    const q = Math.floor( (timestamp - ty) / period )
    debug && console.log( { q, b: baseline.toString(), hours: period / Milliseconds.HOUR })
    const tl = ( ty + q * period ) 
    const timeBucketStartDateTime = new Date( tl )
    return timeBucketStartDateTime
    */
}

/** RATIONALE
 * The previous version did not a) handle width in each case, b) use a predicatable baseline for multiple width * unit, eg a 2 hour bucket may have been
 * calcualted to start at an odd hour ( 23:00 )
 *
 * To resolve this, the new version uses a fixed baseline for each case, calculates the number of whole width * unit periods between the  baseline and the provided timestamp,
 * then returns the last bucket.
 *
 * 1. with timestamp tx and period p = width * unit
 * 2. get timestamp tp, which is 1 period before the timestamp ( tx - p )
 * 3. get the timestamp ty at the beginning of the year containing tp
 * 4. calulate the quotiant q of the time between ty and tx divided by the period ( Math.floor( (tx - ty) / p ))
 * 5. calulate the end timestamp tl of the last whole perod ( ty + q * p )
 *
 */

export type GetTimeBucketEndInput = {
  timeBucketStartTimestamp: number
  timeBucketDef: TimeBucketType
}
/** returns end of time bucket given start from the above fn, adjusted for non-predicatable units */
export const getTimeBucketEnd = ({ timeBucketDef, timeBucketStartTimestamp }: GetTimeBucketEndInput) => {
  const { unit, width } = timeBucketDef
  let timeBucketEnd = timeBucketStartTimestamp
  if ([TimeBucketUnits.MONTH, TimeBucketUnits.YEAR].includes(timeBucketDef.unit)) {
    const refDate = new Date(timeBucketStartTimestamp)
    if (TimeBucketUnits.MONTH === unit) {
      refDate.setUTCMonth(refDate.getUTCMonth() + width)
    } else if (TimeBucketUnits.YEAR === unit) {
      refDate.setUTCFullYear(refDate.getUTCFullYear() + width)
    }
    timeBucketEnd = refDate.valueOf()
  } else {
    timeBucketEnd = timeBucketStartTimestamp + unit * width
  }
  return timeBucketEnd
}
